/**
 * 🧱 Refactor BT Phase 3 — section « SectionEquipeMatriceCreer » de CreerBonTravailDialog.jsx : JSX déplacé TEL QUEL (ex-L866-1082, dédenté de 10) ;
 * variables via `useBTForm()` (BTFormProvider enrichi par `extra`). DOM, data-testid, classes, textes inchangés.
 */
import { AlertTriangle, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useBTForm } from '../../BTFormContext';

export function SectionEquipeMatriceCreer() {
  const {
    employees,
    form,
    leaveConflicts,
    setChefDuJour,
    toggleEmployeJour,
    type,
  } = useBTForm();
  return (
    <>
      {/* 🐛 [Phase v13.46.2 — 2026-07-14] Matrice toujours visible : plus de gate
          `dates_chantier.length >= 2`. L'utilisatrice veut voir les employés
          apparaître dans la matrice DÈS qu'elle les ajoute, même sans dates saisies. */}
      <Card data-testid="matrice-presence-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="w-4 h-4" /> Présence par jour
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Cochez les jours où chaque membre est présent. Un membre décoché un jour ne verra pas le BT dans son planning ce jour-là.
          </p>
        </CardHeader>
        <CardContent>
          {form.dates_chantier.length === 0 ? (
            <div
              className="text-sm text-slate-500 italic p-4 text-center border border-dashed border-slate-200 rounded"
              data-testid="matrice-empty-hint"
            >
              Ajoutez au moins une date de chantier ci-dessous pour marquer la présence de chaque employé.
            </div>
          ) : (
          <div className="overflow-x-auto">
              <table className="w-full text-xs border rounded-lg" data-testid="presence-matrix-table">
                <thead>
                  <tr className="bg-slate-50 border-b">
                    <th className="text-left p-2 font-semibold sticky left-0 bg-slate-50 z-10 min-w-[160px]">Membre</th>
                    {[...form.dates_chantier].sort().map(d => {
                      const dt = new Date(d + 'T12:00:00');
                      const jourNom = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'][dt.getDay()];
                      const moisNom = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'][dt.getMonth()];
                      return (
                        <th key={d} className="p-2 text-center font-semibold whitespace-nowrap" data-testid={`presence-col-${d}`}>
                          <div className="text-slate-500 font-normal">{jourNom}</div>
                          <div>{dt.getDate()} {moisNom}</div>
                        </th>
                      );
                    })}
                    <th className="p-2 text-center font-semibold w-16">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {/* 🆕 Chef du jour — dropdown par jour */}
                  {form.dates_chantier.length >= 2 && (() => {
                    const allChefs = employees.filter(e => (e.role_terrain === 'chef_equipe' || e.type_employe === 'chef_equipe') && e.est_actif !== false);
                    if (allChefs.length === 0) return null;
                    return (
                      <tr className="border-b bg-amber-50/40">
                        <td className="p-2 sticky left-0 bg-amber-50/40 z-10 font-medium" data-testid="row-chef-du-jour">
                          <div className="flex items-center gap-1.5">
                            <span className="text-amber-700">⭐</span>
                            <span className="truncate text-amber-900">Chef du jour</span>
                          </div>
                          <span className="text-[10px] text-amber-700">Sélection par jour</span>
                        </td>
                        {[...form.dates_chantier].sort().map(d => {
                          const chefId = form.chefs_par_jour?.[d] || form.chef_equipe_id || '';
                          return (
                            <td key={d} className="p-1 text-center">
                              <select
                                value={chefId}
                                onChange={(ev) => setChefDuJour(d, ev.target.value)}
                                className="text-[10px] border border-amber-300 rounded px-1 py-0.5 bg-white w-full max-w-[110px] focus:outline-none focus:ring-1 focus:ring-amber-500"
                                data-testid={`chef-du-jour-${d}`}
                              >
                                <option value="">— Aucun —</option>
                                {allChefs.map(c => (
                                  <option key={c.id} value={c.id}>{c.prenom} {c.nom}</option>
                                ))}
                              </select>
                            </td>
                          );
                        })}
                        <td className="p-2 text-center text-xs font-semibold text-amber-700">
                          {Object.keys(form.chefs_par_jour || {}).length}/{form.dates_chantier.length}
                        </td>
                      </tr>
                    );
                  })()}

                  {/* Chef d'équipe */}
                  {form.chef_equipe_id && (() => {
                    const chefDates = form.chef_dates_assignees && form.chef_dates_assignees.length > 0
                      ? form.chef_dates_assignees
                      : form.dates_chantier;
                    return (
                      <tr className="border-b bg-blue-50/50">
                        <td className="p-2 sticky left-0 bg-blue-50/50 z-10 font-medium">
                          <div className="flex items-center gap-1.5">
                            <span className="text-blue-700">👑</span>
                            <span className="truncate">{form.chef_equipe_nom}</span>
                          </div>
                          <span className="text-[10px] text-blue-600">Chef d'équipe</span>
                        </td>
                        {[...form.dates_chantier].sort().map(d => {
                          const present = chefDates.includes(d);
                          return (
                            <td key={d} className="p-1 text-center">
                              <button
                                type="button"
                                onClick={() => toggleEmployeJour(form.chef_equipe_id, d, true)}
                                className={`w-7 h-7 rounded border-2 transition-all ${present ? 'bg-blue-500 border-blue-600 text-white hover:bg-blue-600' : 'bg-white border-slate-300 text-slate-400 hover:border-blue-300'}`}
                                data-testid={`presence-cell-${form.chef_equipe_id}-${d}`}
                                title={present ? `Présent le ${d}` : `Absent le ${d}`}
                              >
                                {present ? '✓' : ''}
                              </button>
                            </td>
                          );
                        })}
                        <td className="p-2 text-center text-xs font-semibold text-blue-700" data-testid={`presence-total-${form.chef_equipe_id}`}>
                          {chefDates.length}/{form.dates_chantier.length}
                        </td>
                      </tr>
                    );
                  })()}

                  {/* Membres de l'équipe */}
                  {form.employes_assignes.filter(e => !e.est_chef && e.employee_id !== form.chef_equipe_id).map(emp => {
                    const empDates = emp.dates_assignees || form.dates_chantier;
                    return (
                      <tr key={emp.employee_id} className="border-b hover:bg-slate-50/50">
                        <td className="p-2 sticky left-0 bg-white hover:bg-slate-50/50 z-10">
                          <div className="truncate">{emp.employee_nom}</div>
                        </td>
                        {[...form.dates_chantier].sort().map(d => {
                          const present = empDates.includes(d);
                          return (
                            <td key={d} className="p-1 text-center">
                              <button
                                type="button"
                                onClick={() => toggleEmployeJour(emp.employee_id, d, false)}
                                className={`w-7 h-7 rounded border-2 transition-all ${present ? 'bg-emerald-500 border-emerald-600 text-white hover:bg-emerald-600' : 'bg-white border-slate-300 text-slate-400 hover:border-emerald-300'}`}
                                data-testid={`presence-cell-${emp.employee_id}-${d}`}
                                title={present ? `Présent le ${d}` : `Absent le ${d}`}
                              >
                                {present ? '✓' : ''}
                              </button>
                            </td>
                          );
                        })}
                        <td className="p-2 text-center text-xs font-semibold text-emerald-700" data-testid={`presence-total-${emp.employee_id}`}>
                          {empDates.length}/{form.dates_chantier.length}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Ligne total jours-personne par jour */}
                  <tr className="bg-slate-100 border-t-2 font-semibold">
                    <td className="p-2 sticky left-0 bg-slate-100 z-10 text-slate-700 text-xs">Total / jour</td>
                    {[...form.dates_chantier].sort().map(d => {
                      let count = 0;
                      if (form.chef_equipe_id) {
                        const chefDates = form.chef_dates_assignees && form.chef_dates_assignees.length > 0
                          ? form.chef_dates_assignees
                          : form.dates_chantier;
                        if (chefDates.includes(d)) count++;
                      }
                      form.employes_assignes.filter(e => !e.est_chef && e.employee_id !== form.chef_equipe_id).forEach(e => {
                        const ed = e.dates_assignees || form.dates_chantier;
                        if (ed.includes(d)) count++;
                      });
                      return (
                        <td key={d} className="p-2 text-center text-slate-700" data-testid={`presence-day-total-${d}`}>{count}</td>
                      );
                    })}
                    <td className="p-2 text-center text-slate-700">
                      {(() => {
                        let total = 0;
                        if (form.chef_equipe_id) {
                          total += (form.chef_dates_assignees && form.chef_dates_assignees.length > 0
                            ? form.chef_dates_assignees
                            : form.dates_chantier).length;
                        }
                        form.employes_assignes.filter(e => !e.est_chef && e.employee_id !== form.chef_equipe_id).forEach(e => {
                          total += (e.dates_assignees || form.dates_chantier).length;
                        });
                        return total;
                      })()}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          {form.dates_chantier.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-slate-500">
              <span>👑 Chef d'équipe</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-emerald-500"></span> Présent</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-white border-2 border-slate-300"></span> Absent (le BT n'apparaîtra pas dans son planning ce jour-là)</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Avertissement congés de l'équipe */}
      {leaveConflicts.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-3" data-testid="leave-conflicts-warning">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
            <p className="text-sm font-semibold text-amber-800">
              Congés approuvés sur les dates sélectionnées
            </p>
          </div>
          <div className="space-y-1">
            {leaveConflicts.map((c, i) => (
              <p key={c.id || c._id || i} className="text-xs text-amber-700 flex items-center gap-1">
                <span>•</span>
                <span className="font-medium">{c.employe_nom || c.employee_nom}</span>
                <span className="text-amber-500">({c.date_debut} → {c.date_fin})</span>
              </p>
            ))}
          </div>
          <p className="text-xs text-amber-600 mt-2">Vous pouvez continuer, mais vérifiez la disponibilité de ces employés.</p>
        </div>
      )}

    </>
  );
}
